import FetchBase from '../Api/FetchBase'
import { observable, action, toJS } from 'mobx'
import { COOKIE_STOAGE } from '../Utils/Storage'
import { COOKIE_SET_TIMER, API_ENDPOINTS } from '../Utils/constants'
import { routes } from '../routes'
import { generateFormData } from '../Utils/helpers'
import {
  generateRoleIntoObject,
  generateUserMetaIntoObject,
} from '../Utils/rules'

export default class LoginStore extends FetchBase {
  @observable user = COOKIE_STOAGE.getItem('user') || {}
  @observable loginLoader = false
  @observable resetPasswordLoader = false
  @observable verifyPasswordLoader = false
  @observable createAccountLoader = false
  @observable updateProfileLoader = false
  @observable genericLoader = false
  @observable myProfileLoader = false
  @observable isHaveGrantPermission = false
  @observable organizationList = []
  @observable verifyInviteLoader = false
  @observable projectExportAllLoader = false
  @observable isAfterLoginAlert = false
  @observable isWizard = false
  @observable antivirusStatus = false

  @action.bound
  setGenericLoader = (value = false) => {
    this.genericLoader = value
  }

  @action.bound
  submitLogin = async (data) => {
    const { email = '', password = '', remember = false } = data || {}

    try {
      this.loginLoader = true

      let response = await this.post(API_ENDPOINTS.LOGIN, {
        email: email ? email?.trim?.() : undefined,
        password,
      })

      if (response.status) {
        const { data = {} } = response
        if (!data) {
          return { result: false, newAccount: false }
        } else {
          const { token, user, organization = [] } = data
          let {
            role,
            superUser = false,
            customerId,
            resellerInfo = false,
          } = user
          role = generateRoleIntoObject(role)
          user['role'] = role || {}
          let isSuperUser = Boolean(superUser)
          let isReseller = Boolean(resellerInfo)
          COOKIE_STOAGE.setItem('token', token, {
            ...(remember ? { maxAge: COOKIE_SET_TIMER } : {}),
          })
          COOKIE_STOAGE.setItem('user', user, {
            ...(remember ? { maxAge: COOKIE_SET_TIMER } : {}),
          })
          COOKIE_STOAGE.setItem('role', role, {
            ...(remember ? { maxAge: COOKIE_SET_TIMER } : {}),
          })
          COOKIE_STOAGE.setItem('remember', remember, {
            ...(remember ? { maxAge: COOKIE_SET_TIMER } : {}),
          })
          COOKIE_STOAGE.setItem('isSuperUser', isSuperUser, {
            ...(remember ? { maxAge: COOKIE_SET_TIMER } : {}),
          })
          COOKIE_STOAGE.setItem('isReseller', isReseller, {
            ...(remember ? { maxAge: COOKIE_SET_TIMER } : {}),
          })
          !isSuperUser &&
            COOKIE_STOAGE.setItem('organizationList', organization, {
              ...(remember ? { maxAge: COOKIE_SET_TIMER } : {}),
            })
          this.isAfterLoginAlert = isSuperUser && customerId ? false : true
          this.user = user
          this.organizationList = organization
          return {
            result: true,
            newAccount: user.isNew,
            isSuperUser: isSuperUser,
            isReseller: isReseller,
            data: data,
          }
        }
      } else {
        return { result: false, newAccount: false }
      }
    } catch (err) {
      return { result: false, newAccount: false }
    } finally {
      this.loginLoader = false
    }
  }

  @action.bound
  routeDeciderByUserPermission = (permissions = {}) => {
    const routeSorter = [
      { DASHBOARD: routes.homepage.generateRoute() },
      { PROJECT: routes.projects.generateRoute() },
      { CUSTOMER: routes.customers.generateRoute() },
      { USER: routes.users.generateRoute() },
      { TEMPLATE: routes.tempspacelist.generateRoute() },
      { TASK_GROUPS: routes.taskgroup.generateRoute() },
      { ROLE: routes.configrole.generateRoute() },
      { REPORTING: routes.reportingtimeline.generateRoute() },
    ]

    for (let index = 0; index < routeSorter.length; index++) {
      let visitObject = routeSorter[index] || {}
      let visitKey = Object.keys(visitObject)?.[0] || undefined
      if (visitKey && permissions?.[visitKey]?.CAN_VIEW) {
        return visitObject[visitKey]
      }
    }
    return routes.myprofile.generateRoute(1)
  }

  @action.bound
  routeDeciderReseller = (permissions) => {
    return permissions
      ? routes.resellerhomepage.generateRoute()
      : routes.termsConditionpageforReseller.generateRoute()
  }

  @action.bound
  resetPasswordRequest = async (data = {}) => {
    try {
      this.resetPasswordLoader = true
      data['redirectUrl'] = `${
        window.location.origin
      }${routes.forgotpassword.generateRoute()}`
      let response = await this.post(API_ENDPOINTS.RESET_PASSWORD_REQUEST, data)
      if (response.status) {
        return [true, response.message]
      } else {
        return [false, response.message]
      }
    } catch (err) {
      return [false, '']
    } finally {
      this.resetPasswordLoader = false
    }
  }

  @action.bound
  verifyPasswordRequest = async (data = {}) => {
    try {
      this.verifyPasswordLoader = true
      const { token = '', confirmpassword = '' } = data
      let response = await this.post(API_ENDPOINTS.VERIFY_PASSWORD_REQUEST, {
        token: token,
        newPassword: confirmpassword,
      })
      if (response.status) {
        return true
      } else {
        return false
      }
    } catch (err) {
      return false
    } finally {
      this.verifyPasswordLoader = false
    }
  }

  @action.bound
  changePassword = async (data = {}) => {
    try {
      this.verifyPasswordLoader = true
      const { password = '', confirmpassword = '', userId = '' } = data
      let response = await this.post(API_ENDPOINTS.CHANGE_PASSWORD_REQUEST, {
        userId,
        oldPassword: password,
        newPassword: confirmpassword,
      })
      if (response.status) {
        return true
      } else {
        return false
      }
    } catch (err) {
      return false
    } finally {
      this.verifyPasswordLoader = false
    }
  }

  @action.bound
  onLinePing = async () => {
    try {
      let response = await this.post(API_ENDPOINTS.I_AM_ONLINE)
      if (response.status) {
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    }
  }

  @action.bound
  getMyProfile = async () => {
    try {
      this.myProfileLoader = true
      let response = await this.post(API_ENDPOINTS.GET_MY_PROFILE)

      if (response.status) {
        this.updateProfileLocally(response.data)
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    } finally {
      this.myProfileLoader = false
    }
  }

  @action.bound
  updateProfileLocally = (values) => {
    let userData = { ...this.user, ...values }
    let { role = [] } = userData || {}
    role = generateRoleIntoObject(role)
    userData['role'] = role || {}
    this.user = userData || {}
    let userMeta = generateUserMetaIntoObject(userData?.userMeta)
    let cremember = (COOKIE_STOAGE.getItem('remember') || '') === 'true'
    COOKIE_STOAGE.setItem('user', this.user, {
      ...(cremember ? { maxAge: COOKIE_SET_TIMER } : {}),
    })
    COOKIE_STOAGE.setItem('isCompletedOnboard', userMeta?.completedOnboardId, {
      ...(cremember ? { maxAge: COOKIE_SET_TIMER } : {}),
    })
  }

  @action.bound
  updateProfile = async (values = {}, loader = true) => {
    try {
      if (loader) {
        this.updateProfileLoader = true
      }
      values['email'] = values['email']?.trim?.() || undefined
      let response = await this.postWithFormData(
        API_ENDPOINTS.UPDATE_PROFILE,
        generateFormData({
          ...values,
        })
      )
      if (response.status) {
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    } finally {
      this.updateProfileLoader = false
    }
  }

  @action.bound
  setOrganization = async (orgId) => {
    try {
      let response = await this.post(API_ENDPOINTS.SET_MY_ORG, { orgId: orgId })
      if (response.status) {
        let cremember = (COOKIE_STOAGE.getItem('remember') || '') === 'true'
        let user = response.data || {}

        let { role = [], customerId, userMeta = [] } = user
        role = generateRoleIntoObject(role)
        user['role'] = role || {}
        userMeta = generateUserMetaIntoObject(userMeta)
        COOKIE_STOAGE.setItem('user', user, {
          ...(cremember ? { maxAge: COOKIE_SET_TIMER } : {}),
        })
        COOKIE_STOAGE.setItem('role', role, {
          ...(cremember ? { maxAge: COOKIE_SET_TIMER } : {}),
        })
        COOKIE_STOAGE.setItem(
          'isCompletedOnboard',
          userMeta?.completedOnboardId,
          {
            ...(cremember ? { maxAge: COOKIE_SET_TIMER } : {}),
          }
        )
        this.isAfterLoginAlert = customerId ? false : true
        this.user = user
        this.isWizard = true
        // this.orgwizardDetails = response?.data?.wizardDetails || []
        return [true, response?.data]
      }
      return [false, undefined]
    } catch {
      return [false, undefined]
    }
  }

  @action.bound
  setCloseAfterLoginAlert = async () => {
    try {
      this.isAfterLoginAlert = false
      return true
    } catch {
      return false
    }
  }

  @action.bound
  verifyInviteRequest = async (data = {}) => {
    try {
      this.verifyInviteLoader = true
      const { token = '' } = data
      let response = await this.post(API_ENDPOINTS.VERIFY_INVITED_USER, {
        token: token,
      })
      if (response.status) {
        return [true, response.data]
      }
    } catch (err) {
      return [true, undefined]
    } finally {
      this.verifyInviteLoader = false
    }
  }

  @action.bound
  downloadProjectExportAll = async (data = {}) => {
    try {
      this.projectExportAllLoader = true
      const { id = '', userId = '' } = data
      let response = await this.post(API_ENDPOINTS.EXPORT_ALL_DOWNLOAD, {
        id: id,
        userId: userId,
      })
      if (response.status) {
        return [true, response.data]
      }
    } catch (err) {
      return [true, undefined]
    } finally {
      this.projectExportAllLoader = false
    }
  }

  @action.bound
  getAntivirusStatus = async () => {
    try {
      const res = await this.get(API_ENDPOINTS.ANTIVIRUS_SCANNER_STATUS)
      if (res?.status) {
        this.antivirusStatus = res?.data?.status
        return [true, res.data?.status]
      }
    } catch (err) {
      return [false, undefined]
    }
  }

  @action.bound
  setAntivirusStatus = async (value) => {
    try {
      const res = await this.get(API_ENDPOINTS.ANTIVIRUS_SCANNER, {
        scan: value,
      })
      if (res?.status) {
        this.antivirusStatus = res?.data?.status
        return [true, res.data?.status]
      }
    } catch (err) {
      return [false, undefined]
    }
  }

  @action.bound
  signOut = async (data = {}) => {
    let cToken = COOKIE_STOAGE.getItem('token')
    let cIsSuperUser = COOKIE_STOAGE.getItem('isSuperUser')
    let cIsisReseller = COOKIE_STOAGE.getItem('isReseller')
    let cOrganizationId = COOKIE_STOAGE.getItem('organizationId')
    let cIsAfterLoginAlert = COOKIE_STOAGE.getItem('isAfterLoginAlert')
    let cIsCompletedOnboard = COOKIE_STOAGE.getItem('isCompletedOnboard')
    let cUser = COOKIE_STOAGE.getItem('user')
    let cRole = COOKIE_STOAGE.getItem('role')
    let cremember = COOKIE_STOAGE.getItem('remember')
    let oUser = this.user
    let OorganizationList = this.organizationList

    try {
      this.user = {}
      this.organizationList = []
      COOKIE_STOAGE.removeItem('token')
      COOKIE_STOAGE.removeItem('isSuperUser')
      COOKIE_STOAGE.removeItem('isReseller')
      COOKIE_STOAGE.removeItem('organizationId')
      COOKIE_STOAGE.removeItem('isAfterLoginAlert')
      COOKIE_STOAGE.removeItem('isCompletedOnboard')
      COOKIE_STOAGE.removeItem('organizationList')
      COOKIE_STOAGE.removeItem('user')
      COOKIE_STOAGE.removeItem('role')
      COOKIE_STOAGE.removeItem('remember')

      sessionStorage.removeItem('token')
      sessionStorage.removeItem('isSuperUser')
      sessionStorage.removeItem('isReseller')
      sessionStorage.removeItem('organizationId')
      sessionStorage.removeItem('isAfterLoginAlert')
      sessionStorage.removeItem('isCompletedOnboard')
      sessionStorage.removeItem('organizationList')
      sessionStorage.removeItem('user')
      sessionStorage.removeItem('role')
      sessionStorage.removeItem('remember')
      this.user = undefined

      return true
    } catch (err) {
      this.user = oUser

      this.organizationList = OorganizationList
      COOKIE_STOAGE.setItem('token', cToken)
      COOKIE_STOAGE.setItem('isSuperUser', cIsSuperUser)
      COOKIE_STOAGE.setItem('isReseller', cIsisReseller)
      COOKIE_STOAGE.setItem('organizationId', cOrganizationId)
      COOKIE_STOAGE.setItem('isAfterLoginAlert', cIsAfterLoginAlert)
      COOKIE_STOAGE.setItem('isCompletedOnboard', cIsCompletedOnboard)
      COOKIE_STOAGE.setItem('role', cRole)
      COOKIE_STOAGE.setItem('remember', cremember)
      return false
    }
  }
}
